"use client";

import { Check } from "lucide-react";
import React from "react";
import { Separator } from "../../shadcn/ui/separator";
import { cn } from "@punchup/utils/cn";

export interface StepperStep {
  label: string;
}

export interface StepperProps {
  steps: StepperStep[];
  activeStepIndex: number;
  onStepClick?: (stepIndex: number) => void;
  showLabels?: boolean;
  className?: string;
}

export const Stepper = ({
  steps,
  activeStepIndex,
  onStepClick,
  showLabels,
  className,
}: StepperProps) => {
  return (
    <div className={className}>
      <div className="flex flex-row items-center justify-between w-full">
        {steps.map((step, index) => {
          const stepNumber = index + 1; // index begins at 0
          const isFirstStep = index === 0;
          const isLastStep = index === steps.length - 1;

          // is this step active?
          // consider active to be: current step or a previous step
          const stepIsActive = activeStepIndex >= index;
          const stepIsCurrent = activeStepIndex === index;

          const previousStepIsActive = activeStepIndex > index;

          return (
            <React.Fragment key={step.label}>
              <div className="flex flex-col items-center">
                <div className="flex flex-row items-center justify-center w-full">
                  {/* consider this the "left" line of the step indicator */}
                  <Separator
                    className={cn(
                      "flex-1 self-start mt-[15px] bg-[#C9CACB] transition-colors duration-200 motion-reduce:transition-none",
                      isFirstStep
                        ? "bg-transparent"
                        : stepIsActive && "bg-primary"
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => onStepClick?.(index)}
                    disabled={!onStepClick}
                    className={cn(
                      "p-[1px] rounded-full flex flex-row items-center justify-center transition-colors duration-200 motion-reduce:transition-none",
                      stepIsCurrent
                        ? "bg-primary"
                        : stepIsActive
                          ? "bg-[#FFBFA8]"
                          : "bg-[#C9CACB]",
                      !onStepClick && "cursor-default"
                    )}
                  >
                    <div className="bg-white p-0.5 rounded-full">
                      <div
                        className={cn(
                          "flex flex-col items-center justify-center w-6 h-6 rounded-full p-1 font-semibold text-[13px] select-none leading-none tracking-normal transition-colors duration-200 motion-reduce:transition-none",
                          stepIsActive
                            ? "bg-primary text-white"
                            : "bg-[#F4F4F4] text-[#939393]"
                        )}
                      >
                        {stepIsActive && !stepIsCurrent ? (
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                        ) : (
                          <span>{stepNumber}</span>
                        )}
                      </div>
                    </div>
                  </button>
                  {/* consider this the first "right" line of the step indicator */}
                  <Separator
                    className={cn(
                      "bg-[#C9CACB] flex-1 self-start mt-[15px] transition-colors duration-200 motion-reduce:transition-none",
                      // always hide the "right" line of the step indicator on the last step
                      isLastStep
                        ? "bg-transparent"
                        : previousStepIsActive && "bg-primary"
                    )}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onStepClick?.(index)}
                  disabled={!onStepClick}
                  className={!onStepClick ? "cursor-default" : undefined}
                >
                  {showLabels && (
                    <p
                      className={cn(
                        "mt-2 text-xs transition-colors duration-200 motion-reduce:transition-none",
                        stepIsActive
                          ? "text-primary font-medium"
                          : "text-[#939393] font-light"
                      )}
                    >
                      {step.label}
                    </p>
                  )}
                </button>
              </div>
              {/* consider this the second "right" line of the step indicator */}
              {!isLastStep && (
                <Separator
                  className={cn(
                    "bg-[#C9CACB] flex-1 self-start mt-[15px] transition-colors duration-200 motion-reduce:transition-none",
                    // always hide the "right" line of the step indicator on the last step
                    isLastStep
                      ? "bg-transparent"
                      : previousStepIsActive && "bg-primary"
                  )}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
